import { useCallback, useEffect, useRef, useState } from 'react';

import { suppressTextSelection } from './Helpers';

const DEFAULT_DIRECTION = "bottom-left"
const DEFAULT_POSITION = { x: 0, y: 0 }
const EMPTY_FUN = () => {}

export default (() => {
  const fitMinMax = (value, min, max) => {
    if(max < min) {
      return min
    }

    return Math.min(Math.max(value, min), max)
  }
  const getPositionFromDragData = data => ({ x: data.x, y: data.y })
  const getPanelBounds = ({ containerNode, currentPosition, panelNode }) => {
    if(!containerNode || !panelNode) {
      return undefined
    }

    const panelRect = panelNode.getBoundingClientRect(),
          containerRect = containerNode.getBoundingClientRect(),
          baseLeft = panelRect.left - currentPosition.x,
          baseTop = panelRect.top - currentPosition.y

    return {
      left: containerRect.left - baseLeft,
      right: containerRect.right - baseLeft - panelRect.width,
      top: containerRect.top - baseTop,
      bottom: containerRect.bottom - baseTop - panelRect.height
    }
  }
  const getPanelBoundsBySelector = ({ currentPosition, panelNode, selector }) => getPanelBounds({ containerNode: selector ? document.querySelector(selector) : undefined, currentPosition, panelNode })
  const usePanelBoundsBySelector = ({ selector }) => useCallback(({ currentPosition, panelNode }) => getPanelBoundsBySelector({ currentPosition, panelNode, selector }), [selector])
  
  const getClampedPanelPosition = ({ currentPosition, nextPosition, panelNode, resolvePanelBounds }) => {
    const bounds = resolvePanelBounds?.({ currentPosition, panelNode })
    if(!bounds) {
      return nextPosition
    }

    const clampedPosition = {
      x: fitMinMax(nextPosition.x, bounds.left, bounds.right),
      y: fitMinMax(nextPosition.y, bounds.top, bounds.bottom)
    }
    if(clampedPosition.x === currentPosition.x && clampedPosition.y === currentPosition.y) {
      return currentPosition
    }

    return clampedPosition
  }
  const useDraggablePanelPosition = ({ panelRef, resolvePanelBounds = undefined }) => {
    const [panelPosition, setPanelPosition] = useState(DEFAULT_POSITION),
          [isDragging, setIsDragging] = useState(false),
          [dragBounds, setDragBounds] = useState(),
          clampPanelPosition = useCallback(({ currentPosition, nextPosition }) => getClampedPanelPosition({
            currentPosition,
            nextPosition,
            panelNode: panelRef.current,
            resolvePanelBounds
          }), [panelRef, resolvePanelBounds]),
          commitPanelPosition = useCallback(nextPosition => setPanelPosition(clampPanelPosition({ currentPosition: nextPosition, nextPosition })), [clampPanelPosition]),
          stopDragging = useCallback(() => {
            setDragBounds(undefined)
            setIsDragging(false)
          }, []),
          onStart = useCallback(() => {
            setDragBounds(resolvePanelBounds?.({ currentPosition: panelPosition, panelNode: panelRef.current }))
            setIsDragging(true)
          }, [panelPosition, panelRef, resolvePanelBounds]),
          onStop = useCallback((_, data) => {
            commitPanelPosition(getPositionFromDragData(data))
            stopDragging()
          }, [commitPanelPosition, stopDragging])

    useEffect(() => {
      if(!resolvePanelBounds) {
        return
      }

      let animationFrameId
      const clampCurrentPanelPosition = () => setPanelPosition(currentPosition => clampPanelPosition({
        currentPosition,
        nextPosition: currentPosition
      }))
      const schedulePanelPositionClamp = () => {
        cancelAnimationFrame(animationFrameId)
        animationFrameId = requestAnimationFrame(clampCurrentPanelPosition)
      }

      schedulePanelPositionClamp()
      window.addEventListener("resize", schedulePanelPositionClamp)

      return () => {
        cancelAnimationFrame(animationFrameId)
        window.removeEventListener("resize", schedulePanelPositionClamp)
      }
    }, [clampPanelPosition, resolvePanelBounds])

    return { clampPanelPosition, dragBounds, isDragging, onStart, onStop, panelPosition, setPanelPosition }
  }

  const usePanelSize = ({ getInitialPanelSize, panelRef }) => {
    const panelSizeRef = useRef(getInitialPanelSize()),
          setPanelSize = useCallback(({ height, width }) => {
            panelSizeRef.current = { height, width }
            if(panelRef.current) {
              panelRef.current.style.height = `${height}px`
              panelRef.current.style.width = `${width}px`
            }
          }, [panelRef])

    return { panelSizeRef, setPanelSize }
  }

  const usePanelPositionSync = ({ clampPanelPosition = undefined, setPanelPosition }) => useCallback(nextPosition => setPanelPosition(currentPosition => {
    const resolvedNextPosition = typeof nextPosition === "function" ? nextPosition(currentPosition) : nextPosition
    return clampPanelPosition ? clampPanelPosition({ currentPosition, nextPosition: resolvedNextPosition }) : resolvedNextPosition
  }), [clampPanelPosition, setPanelPosition])

  const useAutoPanelSize = ({ dependencyKey = "", formRef, getPanelSize, isAutoSizeLockedRef = undefined, observeChildrenResize = false, onAfterSync = EMPTY_FUN, setPanelSize }) => {
    useEffect(() => {
      const formNode = formRef.current?.form?.current
      if(!formNode) {
        return
      }

      let animationFrameId
      const syncPanelSize = () => {
              if(isAutoSizeLockedRef?.current) {
                return
              }
              setPanelSize(getPanelSize(formNode))
              onAfterSync()
            },
            schedulePanelSizeSync = () => {
              cancelAnimationFrame(animationFrameId)
              animationFrameId = requestAnimationFrame(syncPanelSize)
            }

      syncPanelSize()
      const mutationObserver = new MutationObserver(schedulePanelSizeSync),
            resizeObserver = observeChildrenResize ? new ResizeObserver(schedulePanelSizeSync) : undefined

      mutationObserver.observe(formNode, { childList: true, subtree: true, characterData: true })
      observeChildrenResize && Array.from(formNode.children).forEach(child => resizeObserver.observe(child))

      return () => {
        cancelAnimationFrame(animationFrameId)
        mutationObserver.disconnect()
        resizeObserver?.disconnect()
      }
    }, [dependencyKey, formRef, getPanelSize, isAutoSizeLockedRef, observeChildrenResize, onAfterSync, setPanelSize])
  }
  
  const usePanelResize = ({ getPanelNodeSize = ({ panelNode, panelSize }) => ({ height: panelNode?.getBoundingClientRect().height || panelSize.height, width: panelNode?.getBoundingClientRect().width || panelSize.width }), getResizedPanelState, panelPosition, panelRef, panelSizeRef, setPanelSize, updatePanelPosition }) => {
    const resizeStartRef = useRef({ direction: DEFAULT_DIRECTION, pointer: DEFAULT_POSITION, position: DEFAULT_POSITION, size: panelSizeRef.current }),
          cleanupRef = useRef(EMPTY_FUN),
          clearListeners = useCallback(() => {
            cleanupRef.current()
            cleanupRef.current = EMPTY_FUN
          }, []),
          onResizeMove = useCallback(event => {
            const nextPanelState = getResizedPanelState({
              direction: resizeStartRef.current.direction,
              event,
              start: resizeStartRef.current
            })

            setPanelSize(nextPanelState)
            updatePanelPosition({ x: nextPanelState.x, y: nextPanelState.y })
          }, [getResizedPanelState, setPanelSize, updatePanelPosition]),
          onResizeEnd = useCallback(() => {
            clearListeners()
          }, [clearListeners]),
          onResizeStart = useCallback((direction, event) => {
            // Like dragging the panel, holding the mouse button down and moving it must not select text on the page
            event.preventDefault()
            event.stopPropagation()
            resizeStartRef.current = {
              direction,
              pointer: { x: event.clientX, y: event.clientY },
              position: panelPosition,
              size: getPanelNodeSize({ panelNode: panelRef.current, panelSize: panelSizeRef.current })
            }

            clearListeners()
            const handleNode = event.currentTarget,
                  { pointerId } = event,
                  restoreTextSelection = suppressTextSelection()

            handleNode?.setPointerCapture?.(pointerId)
            window.addEventListener("pointermove", onResizeMove)
            window.addEventListener("pointerup", onResizeEnd, { once: true })
            window.addEventListener("pointercancel", onResizeEnd, { once: true })
            cleanupRef.current = () => {
              window.removeEventListener("pointermove", onResizeMove)
              window.removeEventListener("pointerup", onResizeEnd)
              window.removeEventListener("pointercancel", onResizeEnd)
              if(handleNode?.hasPointerCapture?.(pointerId)) {
                handleNode.releasePointerCapture(pointerId)
              }
              restoreTextSelection()
            }
          }, [clearListeners, getPanelNodeSize, onResizeEnd, onResizeMove, panelPosition, panelRef, panelSizeRef])

    // Also clean up when the panel unmounts mid-resize, so the page is never left unselectable
    useEffect(() => clearListeners, [clearListeners])

    return { onResizeStart }
  }

  return {
    getPanelBounds,
    getPanelBoundsBySelector,
    useAutoPanelSize,
    usePanelBoundsBySelector,
    useDraggablePanelPosition,
    usePanelPositionSync,
    usePanelResize,
    usePanelSize
  }
})()
